/**
 * Vendor → Addresses tab
 * Shows all addresses for the vendor: HQ, Remit-To, Ship-From, 1099, Billing, Other.
 * Supports add, edit, delete, and promoting an address to default-for-role.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { partyAddressesQueryOptions, partyAddressKeys } from "@/lib/queries";
import { api } from "@/lib/api";
import type {
  PartyAddress,
  AddressRole,
  PartyAddressCreate,
  PartyAddressUpdate,
} from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/addresses")({
  component: VendorAddressesTab,
});

const ROLE_LABEL: Record<AddressRole, string> = {
  hq: "HQ / Primary",
  remit_to: "Remit-To",
  ship_from: "Ship-From",
  "1099": "1099 Address",
  billing: "Billing",
  other: "Other",
};

const ROLE_COLOR: Record<AddressRole, string> = {
  hq: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  remit_to:
    "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  ship_from:
    "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  "1099": "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  billing:
    "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  other:
    "bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300",
};

const ALL_ROLES: AddressRole[] = [
  "hq",
  "remit_to",
  "ship_from",
  "1099",
  "billing",
  "other",
];

const EMPTY_FORM: PartyAddressCreate = {
  address_role: "hq",
  is_default_for_role: false,
  label: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "Canada",
  phone: "",
  email: "",
  contact_name: "",
};

function AddressCard({
  addr,
  onEdit,
  onDelete,
  onSetDefault,
}: {
  addr: PartyAddress;
  onEdit: (a: PartyAddress) => void;
  onDelete: (id: string) => void;
  onSetDefault: (id: string) => void;
}) {
  return (
    <div
      className={`bg-white dark:bg-gray-900 border rounded-lg p-4 ${
        addr.is_default_for_role
          ? "border-blue-400 dark:border-blue-600"
          : "border-slate-200 dark:border-gray-800"
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`px-2 py-0.5 text-xs rounded font-medium ${ROLE_COLOR[addr.address_role]}`}
          >
            {ROLE_LABEL[addr.address_role]}
          </span>
          {addr.is_default_for_role && (
            <span className="px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400 border border-blue-200 dark:border-blue-800">
              Default
            </span>
          )}
          {addr.label && (
            <span className="text-xs text-slate-500 dark:text-slate-400 italic">
              {addr.label}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!addr.is_default_for_role && (
            <button
              onClick={() => onSetDefault(addr.id)}
              className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 px-1.5 py-0.5 rounded hover:bg-blue-50 dark:hover:bg-blue-950"
            >
              Set default
            </button>
          )}
          <button
            onClick={() => onEdit(addr)}
            className="text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 px-1.5 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-gray-800"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(addr.id)}
            className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 px-1.5 py-0.5 rounded hover:bg-red-50 dark:hover:bg-red-950"
          >
            Delete
          </button>
        </div>
      </div>
      <div className="text-sm text-slate-800 dark:text-slate-200 space-y-0.5">
        <div>{addr.address_line1}</div>
        {addr.address_line2 && <div>{addr.address_line2}</div>}
        <div>
          {[addr.city, addr.state, addr.postal_code].filter(Boolean).join(", ")}
        </div>
        <div className="text-slate-500 dark:text-slate-400">{addr.country}</div>
      </div>
      {(addr.contact_name || addr.phone || addr.email) && (
        <div className="mt-2 pt-2 border-t border-slate-100 dark:border-gray-800 text-xs text-slate-500 dark:text-slate-400 space-y-0.5">
          {addr.contact_name && <div>👤 {addr.contact_name}</div>}
          {addr.phone && <div>📞 {addr.phone}</div>}
          {addr.email && <div>✉️ {addr.email}</div>}
        </div>
      )}
    </div>
  );
}

function AddressForm({
  initial,
  onSave,
  onCancel,
  saving,
}: {
  initial: PartyAddressCreate;
  onSave: (data: PartyAddressCreate) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [form, setForm] = useState<PartyAddressCreate>(initial);
  const set = (k: keyof PartyAddressCreate, v: unknown) =>
    setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="bg-white dark:bg-gray-900 border border-blue-300 dark:border-blue-700 rounded-lg p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Role
          </label>
          <select
            value={form.address_role}
            onChange={(e) => set("address_role", e.target.value as AddressRole)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          >
            {ALL_ROLES.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Label (optional)
          </label>
          <input
            type="text"
            value={form.label ?? ""}
            onChange={(e) => set("label", e.target.value || null)}
            placeholder="e.g. Edmonton Office"
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          Street address *
        </label>
        <input
          type="text"
          value={form.address_line1}
          onChange={(e) => set("address_line1", e.target.value)}
          className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          Suite / unit
        </label>
        <input
          type="text"
          value={form.address_line2 ?? ""}
          onChange={(e) => set("address_line2", e.target.value || null)}
          className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            City *
          </label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Province / State
          </label>
          <input
            type="text"
            value={form.state ?? ""}
            onChange={(e) => set("state", e.target.value || null)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Postal code
          </label>
          <input
            type="text"
            value={form.postal_code ?? ""}
            onChange={(e) => set("postal_code", e.target.value || null)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
          Country
        </label>
        <input
          type="text"
          value={form.country}
          onChange={(e) => set("country", e.target.value)}
          className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
        />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Contact name
          </label>
          <input
            type="text"
            value={form.contact_name ?? ""}
            onChange={(e) => set("contact_name", e.target.value || null)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Phone
          </label>
          <input
            type="text"
            value={form.phone ?? ""}
            onChange={(e) => set("phone", e.target.value || null)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
            Email
          </label>
          <input
            type="text"
            value={form.email ?? ""}
            onChange={(e) => set("email", e.target.value || null)}
            className="w-full border border-slate-300 dark:border-gray-700 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-slate-900 dark:text-slate-100"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="is_default"
          type="checkbox"
          checked={form.is_default_for_role}
          onChange={(e) => set("is_default_for_role", e.target.checked)}
          className="h-4 w-4 rounded border-slate-300 text-blue-600"
        />
        <label
          htmlFor="is_default"
          className="text-sm text-slate-700 dark:text-slate-300"
        >
          Set as default for this role
        </label>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onSave(form)}
          disabled={saving || !form.address_line1 || !form.city}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm border border-slate-300 dark:border-gray-700 rounded text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function VendorAddressesTab() {
  const { vendorId } = Route.useParams();
  const qc = useQueryClient();
  const addressesQ = useQuery(partyAddressesQueryOptions(vendorId));
  const addresses: PartyAddress[] = addressesQ.data ?? [];

  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: partyAddressKeys.list(vendorId) });

  const createMut = useMutation({
    mutationFn: (data: PartyAddressCreate) =>
      api<PartyAddress>(`/api/parties/${vendorId}/addresses`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      setShowAdd(false);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: PartyAddressUpdate }) =>
      api<PartyAddress>(`/api/parties/${vendorId}/addresses/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      invalidate();
      setEditingId(null);
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/parties/${vendorId}/addresses/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const setDefaultMut = useMutation({
    mutationFn: (id: string) =>
      api<PartyAddress>(
        `/api/parties/${vendorId}/addresses/${id}/set-default-for-role`,
        { method: "POST" },
      ),
    onSuccess: () => {
      invalidate();
      setError(null);
    },
    onError: (e: Error) => setError(e.message),
  });

  const editingAddr = editingId
    ? addresses.find((a) => a.id === editingId)
    : null;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
            Addresses
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            {addresses.length} address{addresses.length !== 1 ? "es" : ""} on
            file
          </p>
        </div>
        {!showAdd && (
          <button
            onClick={() => {
              setShowAdd(true);
              setEditingId(null);
            }}
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            + Add address
          </button>
        )}
      </div>

      {error && (
        <div className="mb-3 text-sm text-red-600 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-3 py-2">
          {error}
        </div>
      )}

      {addressesQ.isLoading && (
        <div className="text-sm text-slate-500 dark:text-slate-400">
          Loading…
        </div>
      )}

      {showAdd && (
        <div className="mb-4">
          <AddressForm
            initial={EMPTY_FORM}
            onSave={(data) => createMut.mutate(data)}
            onCancel={() => setShowAdd(false)}
            saving={createMut.isPending}
          />
        </div>
      )}

      {!addressesQ.isLoading && addresses.length === 0 && !showAdd && (
        <div className="text-sm text-slate-400 dark:text-slate-500">
          No addresses on file. Click &ldquo;+ Add address&rdquo; to add one.
        </div>
      )}

      <div className="space-y-3">
        {addresses.map((addr) =>
          editingId === addr.id && editingAddr ? (
            <AddressForm
              key={addr.id}
              initial={{
                address_role: editingAddr.address_role,
                is_default_for_role: editingAddr.is_default_for_role,
                label: editingAddr.label,
                address_line1: editingAddr.address_line1,
                address_line2: editingAddr.address_line2,
                city: editingAddr.city,
                state: editingAddr.state,
                postal_code: editingAddr.postal_code,
                country: editingAddr.country,
                phone: editingAddr.phone,
                email: editingAddr.email,
                contact_name: editingAddr.contact_name,
              }}
              onSave={(data) =>
                updateMut.mutate({ id: addr.id, data })
              }
              onCancel={() => setEditingId(null)}
              saving={updateMut.isPending}
            />
          ) : (
            <AddressCard
              key={addr.id}
              addr={addr}
              onEdit={(a) => {
                setEditingId(a.id);
                setShowAdd(false);
              }}
              onDelete={(id) => {
                if (confirm("Delete this address?")) deleteMut.mutate(id);
              }}
              onSetDefault={(id) => setDefaultMut.mutate(id)}
            />
          ),
        )}
      </div>
    </div>
  );
}
